import React, { useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Lightbulb,
  MessageSquare,
  SlidersHorizontal,
} from 'lucide-react';
import { AppPhase, type GradedWork } from '../../../types';
import type { GradeHubSession } from '../../../lib/gradeHub/session';
import { Button } from '../../design-system/buttons';
import { FirstRunRoadmap } from '../onboarding/FirstRunRoadmap';
import { dismissFirstRunRoadmap, isFirstRunRoadmapDismissed } from '../../../lib/appShellFirstRun';
import {
  buildDashboardView,
  type DashboardSyncInfo,
  type DashboardTone,
} from './buildDashboardView';

/**
 * Dashboard V1 (Phase 1C) — a calm teacher launchpad. Behind VITE_APP_SHELL.
 *
 * Presentational: it receives raw-but-safe-or-strippable inputs and runs the
 * pure {@link buildDashboardView} helper internally, which guarantees no
 * student-identifying or content data is ever rendered. No persistence, no
 * fake history/plans/communication, no auto actions.
 */
export interface DashboardHomeProps {
  educatorName?: string | null;
  todayLabel?: string | null;
  googleConnected?: boolean | null;
  reconnectRequired?: boolean;
  sync?: DashboardSyncInfo | null;
  resumeSession?: GradeHubSession | null;
  history?: GradedWork[];
  /** Launches the existing focused grading workflow (SCAN_WORKFLOW). */
  onStartGrading: () => void;
  /** Navigates to another app-shell phase. */
  onNavigate: (phase: AppPhase) => void;
  /** Opens the lightweight demo (Onboarding 1C). Provided only when enabled. */
  onTryDemo?: () => void;
}

const toneText: Record<DashboardTone, string> = {
  ok: 'text-dg-semantic-success dark:text-emerald-400',
  info: 'text-dg-primary-600 dark:text-violet-300',
  muted: 'text-dg-fg-muted dark:text-slate-400',
  warning: 'text-amber-700 dark:text-amber-300',
  danger: 'text-dg-semantic-error dark:text-rose-300',
};

function formatTimestamp(ts: number): string {
  try {
    return new Date(ts).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

const ENTRY_CARDS: ReadonlyArray<{
  phase: AppPhase;
  title: string;
  body: string;
  cta: string;
  Icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
}> = [
  {
    phase: AppPhase.PLAN_HOME,
    title: 'Teaching plans',
    body: 'Plan instruction from reviewed student work.',
    cta: 'Open Plan',
    Icon: Lightbulb,
  },
  {
    phase: AppPhase.COMMUNICATE_HOME,
    title: 'Communicate',
    body: 'Create reviewed drafts and summaries from student work.',
    cta: 'Open Communicate',
    Icon: MessageSquare,
  },
  {
    phase: AppPhase.MANAGE_HOME,
    title: 'Manage',
    body: 'Organize classes, assignments, rubrics, and connections.',
    cta: 'Open Manage',
    Icon: SlidersHorizontal,
  },
];

export const DashboardHome: React.FC<DashboardHomeProps> = ({
  educatorName,
  todayLabel,
  googleConnected,
  reconnectRequired,
  sync,
  resumeSession,
  history,
  onStartGrading,
  onNavigate,
  onTryDemo,
}) => {
  const view = useMemo(
    () =>
      buildDashboardView({
        educatorName,
        todayLabel,
        googleConnected,
        reconnectRequired,
        sync,
        resumeSession,
        history,
      }),
    [educatorName, todayLabel, googleConnected, reconnectRequired, sync, resumeSession, history]
  );

  // First-run onboarding roadmap: shown only when the teacher has no real safe
  // grading history and no active/continue session, and has not hidden it.
  // Detection reads only privacy-safe derived booleans from the view model.
  const [roadmapDismissed, setRoadmapDismissed] = useState(() => isFirstRunRoadmapDismissed());
  const isFirstRun = view.recentEmpty && !view.continueCard;
  const showRoadmap = isFirstRun && !roadmapDismissed;

  const handleDismissRoadmap = () => {
    dismissFirstRunRoadmap();
    setRoadmapDismissed(true);
  };

  return (
    <main data-dg-app-shell-phase="home" className="flex-1 min-h-0 overflow-y-auto w-full">
      <div className="mx-auto w-full max-w-3xl px-5 pt-8 pb-[calc(5rem+env(safe-area-inset-bottom))] flex flex-col gap-6">
        {/* 1. Greeting / status */}
        <header className="flex flex-col gap-2">
          <h1 className="text-dg-display font-black text-dg-fg-primary">
            {view.greetingName ? `${view.greeting}, ${view.greetingName}` : view.greeting}
          </h1>
          {view.todayLabel ? (
            <p className="text-dg-body text-dg-fg-secondary">{view.todayLabel}</p>
          ) : null}
          {view.statusItems.length > 0 ? (
            <ul className="flex flex-wrap gap-x-4 gap-y-1">
              {view.statusItems.map((item) => (
                <li key={item.id} className={`text-dg-label font-semibold ${toneText[item.tone]}`}>
                  {item.text}
                </li>
              ))}
            </ul>
          ) : null}
        </header>

        {/* 1.5 First-run onboarding roadmap (Connect → Scan → Review) */}
        {showRoadmap ? (
          <FirstRunRoadmap
            onStartGrading={onStartGrading}
            onDismiss={handleDismissRoadmap}
            onTryDemo={onTryDemo}
          />
        ) : null}

        {/* 2. Primary action card */}
        <section className="rounded-dg-card border border-dg-border-subtle bg-dg-bg-surface p-5 shadow-dg-card dark:border-slate-700 dark:bg-slate-900">
          <h2 className="text-dg-title font-bold text-dg-fg-primary">Start grading</h2>
          <p className="mt-1 text-dg-body text-dg-fg-secondary">
            Scan student work and review feedback.
          </p>
          <div className="mt-4">
            <Button type="button" variant="primary" size="lg" onClick={onStartGrading}>
              Start grading
            </Button>
          </div>
        </section>

        {/* 3. Continue work (only when real safe data exists) */}
        {view.continueCard ? (
          <section
            data-dg-section="continue"
            className="rounded-dg-card border border-dg-border-subtle bg-dg-bg-surface p-5 shadow-dg-control dark:border-slate-700 dark:bg-slate-900"
          >
            <h2 className="text-dg-title font-bold text-dg-fg-primary">Continue grading</h2>
            <p className="mt-1 text-dg-body text-dg-fg-secondary">
              {view.continueCard.courseName} · {view.continueCard.assignmentName}
            </p>
            <p className="mt-0.5 text-dg-label text-dg-fg-muted">
              {view.continueCard.progressText}
              {view.continueCard.reviewText ? ` · ${view.continueCard.reviewText}` : ''}
            </p>
            <div className="mt-4">
              <Button type="button" variant="secondary" size="md" onClick={onStartGrading}>
                Continue grading
              </Button>
            </div>
          </section>
        ) : null}

        {/* 4. Needs attention */}
        <section data-dg-section="needs-attention" aria-label="Needs attention">
          <h2 className="mb-2 text-dg-label font-black uppercase tracking-widest text-dg-fg-tertiary">
            Needs attention
          </h2>
          {view.allSet ? (
            <p className="flex items-center gap-2 text-dg-body text-dg-fg-secondary">
              <CheckCircle2 className="h-4 w-4 text-dg-semantic-success" aria-hidden />
              You’re all set for now.
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {view.needsAttention.map((item) => (
                <li
                  key={item.id}
                  className="flex items-start gap-2 rounded-dg-control border border-dg-border-subtle bg-dg-bg-surface p-3 dark:border-slate-700 dark:bg-slate-900"
                >
                  <AlertTriangle
                    className={`mt-0.5 h-4 w-4 shrink-0 ${toneText[item.tone]}`}
                    aria-hidden
                  />
                  <span className="text-dg-body text-dg-fg-primary">{item.text}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* 5. Recent work (safe summaries only) */}
        <section data-dg-section="recent-work" aria-label="Recent work">
          <h2 className="mb-2 text-dg-label font-black uppercase tracking-widest text-dg-fg-tertiary">
            Recent work
          </h2>
          {view.recentEmpty ? (
            <p className="text-dg-body text-dg-fg-secondary">
              Your first grading session will appear here. Tap Start grading to choose a class,
              assignment, and rubric.
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-dg-border-subtle rounded-dg-card border border-dg-border-subtle bg-dg-bg-surface dark:divide-slate-700 dark:border-slate-700 dark:bg-slate-900">
              {view.recentWork.map((item) => (
                <li key={item.id} className="flex items-center justify-between gap-3 px-4 py-3">
                  <span className="min-w-0">
                    <span className="block truncate text-dg-body font-semibold text-dg-fg-primary">
                      {item.assignmentName}
                    </span>
                    <span className="block truncate text-dg-label text-dg-fg-muted">
                      {item.courseName}
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span
                      className={`block text-dg-label font-semibold ${
                        item.status === 'synced'
                          ? 'text-dg-semantic-success dark:text-emerald-400'
                          : 'text-dg-fg-muted'
                      }`}
                    >
                      {item.status === 'synced' ? 'Synced' : 'Draft'}
                    </span>
                    <span className="block text-dg-label text-dg-fg-muted">
                      {formatTimestamp(item.timestamp)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* 6-8. Workspace entry cards */}
        <section aria-label="Workspaces" className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {ENTRY_CARDS.map(({ phase, title, body, cta, Icon }) => (
            <div
              key={title}
              className="flex flex-col rounded-dg-card border border-dg-border-subtle bg-dg-bg-surface p-4 shadow-dg-control dark:border-slate-700 dark:bg-slate-900"
            >
              <div className="flex items-center gap-2">
                <Icon className="h-5 w-5 text-dg-primary-600 dark:text-violet-300" aria-hidden />
                <h3 className="text-dg-body font-bold text-dg-fg-primary">{title}</h3>
              </div>
              <p className="mt-1 flex-1 text-dg-label text-dg-fg-secondary">{body}</p>
              <div className="mt-3">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => onNavigate(phase)}
                >
                  {cta}
                </Button>
              </div>
            </div>
          ))}
        </section>
      </div>
    </main>
  );
};

export default DashboardHome;
